import { formatMoney } from "../../customer/account/filing/filing.helpers";

// Regras do pedido de protocolo que não dependem da tela (prazo, horário, anexos, pendências, assinantes).

export const NOT_INFORMED = 'não informado';
// Prazo ainda não lido dos autos (termo 6.2): o controle é do advogado.
export const DEADLINE_TO_CONFIRM = 'a confirmar';

// Horário do tribunal: tudo em Brasília, nunca no fuso do computador do cliente.
export const brtNow = (now = Date.now()) => {
    const parts = Object.fromEntries(
        new Intl.DateTimeFormat('en-CA', {
            timeZone: 'America/Sao_Paulo',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23'
        })
            .formatToParts(new Date(now))
            .map((part) => [part.type, part.value])
    );

    return {
        date: `${parts.year}-${parts.month}-${parts.day}`,
        hour: Number(parts.hour),
        minute: Number(parts.minute)
    };
};

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})/;

// "2026-10-14" vira "14/10/2026" sem passar por Date (que puxaria o dia para trás no fuso do Brasil).
export const formatDay = (value) => {
    const match = ISO_DAY.exec(String(value || ''));
    return match ? `${match[3]}/${match[2]}/${match[1]}` : '';
};

export const formatDateTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? ''
        : date.toLocaleString('pt-BR', {
              timeZone: 'America/Sao_Paulo',
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
          });
};

// "15/10 às 18h" ou "15/10 às 18h32", no horário de Brasília.
export const formatAt = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const part = (options) =>
        date.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', ...options });
    const [hour, minute] = part({ hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).split(
        ':'
    );
    return `${part({ day: '2-digit', month: '2-digit' })} às ${Number(hour)}h${
        minute === '00' ? '' : minute
    }`;
};

// Trava do botão antes do clique (a API diz; o 409 continua como defesa): peça já protocolada (nosso pedido ou o
// campo manual "Serviço protocolado em") ou pedido nosso em andamento/agendado. null = pode pedir.
export const filingLockOf = (panel, verbs) => {
    const filed = panel?.alreadyFiled;
    if (filed) {
        const at = formatAt(filed.at);
        return {
            text: `Esta peça já foi ${verbs.doneFem}${at ? ` em ${at}` : ''}.`,
            receiptUrl: /^https:\/\//i.test(String(filed.receiptUrl || ''))
                ? filed.receiptUrl
                : null
        };
    }
    if (panel?.activeFiling)
        return { text: `Já existe um pedido de ${verbs.noun} em andamento.`, receiptUrl: null };
    return null;
};

// Pedido encerrado sem protocolar: mensagem do motivo (a API exige o código). `retry` diz se faz sentido pedir de novo.
// Falha passageira nunca chega aqui: o robô tenta de novo e, esgotado, a equipe assume (o cliente vê "em andamento").
// Tribunal fora do ar também não: o pedido espera ("aguardando o tribunal") e protocolamos quando ele volta.
export const failedNoticeOf = (filing, verbs, courtName) => {
    if (filing?.status !== 'failed')
        return null;
    const court = courtName?.split(' · ')[0] || 'tribunal';
    const action = verbs.action.toLowerCase();
    const notices = {
        JA_PROTOCOLADO: {
            title: `Peça já ${verbs.doneFem} no processo`,
            text: `Ela já aparece como ${verbs.doneFem} no ${court}. Não enviamos de novo para não duplicar.`,
            retry: false
        },
        SOLICITACAO_ARQUIVADA: {
            title: 'Solicitação arquivada',
            text: `A solicitação foi arquivada antes do envio, então não vamos ${action} esta peça.`,
            retry: false
        },
        PECA_ALTERADA: {
            title: 'A peça mudou depois do pedido',
            text: `Houve correção ou nova versão. Peça de novo para ${action}mos a versão atual.`,
            retry: true
        }
    };
    const refund = filing.refundedAt && Number(filing.fee) > 0 ? ` Devolvemos ${formatMoney(filing.fee)} ao seu saldo.` : '';
    const notice = notices[filing.reasonCode] || {
        title: `Não fizemos ${verbs.theNoun}`,
        text: 'Pode pedir de novo quando quiser.',
        retry: true
    };
    return { ...notice, text: `${notice.text}${refund}` };
};

// Pedido agendado (notBefore no futuro): o robô só começa nesse horário.
export const scheduledStartOf = (filing, now = Date.now()) => {
    const time = Date.parse(filing?.notBefore || '');
    return Number.isNaN(time) || time <= now ? null : filing.notBefore;
};

const weekdayOf = (isoDay) => new Date(`${isoDay}T12:00:00Z`).getUTCDay();
const isWeekend = (isoDay) => [0, 6].includes(weekdayOf(isoDay));
const nextWeekdayAfter = (isoDay) => {
    const date = new Date(`${isoDay}T12:00:00Z`);
    do date.setUTCDate(date.getUTCDate() + 1);
    while ([0, 6].includes(date.getUTCDay()));
    return date.toISOString().slice(0, 10);
};

// Véspera corrida ("2026-10-15" vira "2026-10-14"), sem fuso: só a data.
export const dayBefore = (isoDay) => {
    const date = new Date(`${isoDay}T12:00:00Z`);
    date.setUTCDate(date.getUTCDate() - 1);
    return date.toISOString().slice(0, 10);
};

// Fontes trocadas por equivalente no PDF: "A", "A e B", "A, B e C", "A, B, C e outras".
export const fontNoticeNames = (fontNotice) => {
    const names = [
        ...new Set(
            (fontNotice?.fonts || []).map((name) => String(name || '').trim()).filter(Boolean)
        )
    ];
    if (names.length > 3) return `${names.slice(0, 3).join(', ')} e outras`;
    if (names.length > 1) return `${names.slice(0, -1).join(', ')} e ${names[names.length - 1]}`;
    return names[0] || '';
};

// Quem assina o pedido, o principal primeiro (pedido antigo: só signerName).
export const signerNamesOf = (filing) => {
    const names = (Array.isArray(filing?.signers) ? filing.signers : [])
        .map((signer) => String(signer?.name || '').trim())
        .filter(Boolean);
    if (names.length) return [...new Set(names)];
    const single = String(filing?.signerName || '').trim();
    return single ? [single] : [];
};

// Arquivos do comprovante na ordem da API garantida aqui: comprovante, petição, o resto. API antiga: só receipt.
const RECEIPT_ORDER = ['comprovante', 'peticao'];
const receiptRank = (receipt) => {
    const rank = RECEIPT_ORDER.indexOf(receipt?.kind);
    return rank < 0 ? RECEIPT_ORDER.length : rank;
};

export const receiptsOf = (filing) => {
    const listed = (Array.isArray(filing?.receipts) ? filing.receipts : []).filter(
        (item) => item?.url
    );
    if (listed.length) return [...listed].sort((a, b) => receiptRank(a) - receiptRank(b));
    return filing?.receipt?.url ? [{ ...filing.receipt, label: null }] : [];
};

// O nome do botão vem da API (label); sem label, "Comprovante".
export const receiptLabelOf = (receipt) => String(receipt?.label || '').trim() || 'Comprovante';

// Horário único: depois das 20h o pedido vai ao próximo dia útil. Antecedência: aceitamos o pedido até as 23h59 da
// véspera do prazo; no dia do prazo não aceitamos mais.
export const AFTER_HOURS = 20;

export const START_HOUR = 8;

// O painel (API) decide e é recarregado ao abrir a conferência. O relógio só endurece o limite para quem deixou a
// tela aberta (passou da meia-noite); sem o prazo da API (painel antigo), o relógio decide.
export const deadlineState = (deadline, now = Date.now()) => {
    const clock = brtNow(now);
    const day = ISO_DAY.exec(String(deadline?.fim || ''))?.[0] || null;
    const fromApi =
        typeof deadline?.vencido === 'boolean' ||
        typeof deadline?.venceHoje === 'boolean' ||
        !!deadline?.agendadoPara;
    const dueToday = fromApi
        ? !!deadline.venceHoje && !(day && day < clock.date)
        : !!day && day === clock.date;
    const overdue =
        !!deadline?.vencido || (!!day && day < clock.date && (!fromApi || !!deadline.venceHoje));
    const lastDay = day ? dayBefore(day) : null;
    const pastLimit = !!day && clock.date === day;
    const tooClose = !overdue && (!!deadline?.bloqueadoPorHorario || dueToday || pastLimit);
    // Fora do horário (20h às 8h, fim de semana) vai para o próximo dia útil. A API não agenda para o último dia do
    // prazo (nem depois): aí o pedido sai na hora. O relógio cobre o painel aberto
    // antes das 20h (a API agenda no clique); feriado só a API sabe.
    const outsideHours =
        isWeekend(clock.date) || clock.hour >= AFTER_HOURS || clock.hour < START_HOUR;
    const nextDay =
        isWeekend(clock.date) || clock.hour >= AFTER_HOURS
            ? nextWeekdayAfter(clock.date)
            : clock.date;
    const nextBusinessDay =
        !overdue &&
        !dueToday &&
        !tooClose &&
        (!!deadline?.agendadoPara || (outsideHours && (!day || nextDay < day)));

    return {
        known: !!day,
        day,
        dueToday,
        overdue,
        tooClose,
        nextBusinessDay,
        lastDay,
        dueTomorrow: !!lastDay && !overdue && clock.date === lastDay,
        afterHours: clock.hour >= AFTER_HOURS,
        scheduledFor: deadline?.agendadoPara || null
    };
};

// Sem ordem (order: null, não sugerido pela API) vai para o fim.
const orderOf = (document) => Number(document.order) || Number.MAX_SAFE_INTEGER;

// Lista final dos anexos: o que o cliente vê e pode mexer. Documento interno nunca entra; mídia não vai junto.
export const sendableDocuments = (documents, pieceId) =>
    (documents || [])
        .filter((document) => document && document.id !== undefined && document.id !== null)
        .filter(
            (document) =>
                !document.internal &&
                !document.media &&
                document.documentType !== 'main_piece' &&
                Number(document.id) !== Number(pieceId)
        )
        .sort((a, b) => orderOf(a) - orderOf(b));

export const secretSuggestion = (document, caseData) =>
    !!document.secret ||
    (caseData?.sigilosos || []).some((item) => Number(item?.documentId) === Number(document.id));

export const secretReasonOf = (documentId, caseData) =>
    (caseData?.sigilosos || []).find((item) => Number(item?.documentId) === Number(documentId))
        ?.motivo || null;

// Mantém a ordem e o sigilo que o cliente já mexeu; anexos novos (recarga do painel) entram no fim. A API sugere a
// lista (selected: true); os demais documentos da solicitação (selected: false) só entram se o cliente incluir ou
// se ele acabou de anexar aqui (included).
export const mergeAttachments = (
    current,
    documents,
    { pieceId, removed = [], included = [], caseData } = {}
) => {
    const available = sendableDocuments(documents, pieceId);
    const byId = new Map(available.map((document) => [Number(document.id), document]));
    const kept = (current || []).filter((item) => byId.has(Number(item.id)));
    const keptIds = new Set(kept.map((item) => Number(item.id)));
    const removedIds = new Set(removed.map(Number));
    const includedIds = new Set(included.map(Number));
    const added = available
        .filter(
            (document) =>
                !keptIds.has(Number(document.id)) &&
                !removedIds.has(Number(document.id)) &&
                (document.selected !== false || includedIds.has(Number(document.id)))
        )
        .map((document) => ({
            id: Number(document.id),
            secret: secretSuggestion(document, caseData)
        }));

    return [...kept, ...added];
};

// Custas (guia paga OU justiça gratuita): os arquivos da opção escolhida vão para a lista e os da outra saem, como
// o robô faria sem a lista do cliente. O que o cliente tirou à mão não volta.
export const syncAlternatives = (
    current,
    groups,
    { removed = [], preserved = [], caseData, documentsById } = {}
) => {
    const removedIds = new Set(removed.map(Number));
    const idsOf = (items) =>
        items.flatMap((item) => (item.documents || []).map((document) => Number(document.id)));
    const chosenIds = idsOf(groups.flatMap((group) => group.chosenItems || []));
    const preservedIds = new Set(preserved.map(Number));
    const otherIds = new Set(
        idsOf(
            groups.flatMap((group) =>
                group.items.filter((item) => item.alternative !== group.chosen)
            )
        ).filter((id) => !chosenIds.includes(id) && !preservedIds.has(id))
    );
    const kept = (current || []).filter((item) => !otherIds.has(Number(item.id)));
    const keptIds = new Set(kept.map((item) => Number(item.id)));
    const added = [...new Set(chosenIds)]
        .filter((id) => !keptIds.has(id) && !removedIds.has(id) && documentsById?.has(id))
        .map((id) => ({ id, secret: secretSuggestion(documentsById.get(id), caseData) }));
    return added.length || kept.length !== (current || []).length ? [...kept, ...added] : current;
};

// A API aceita até 20 autorizações de até 300 caracteres.
export const authorizedMissingOf = (descriptions) =>
    descriptions.slice(0, 20).map((text) => String(text).slice(0, 300));

export const moveItem = (list, index, offset) => {
    const target = index + offset;
    if (target < 0 || target >= list.length) return list;
    const next = [...list];
    [next[index], next[target]] = [next[target], next[index]];
    return next;
};

// Pendências que o cliente resolve antes do clique. Sem a lista da API, montamos pelos dados do caso.
export const pendenciesOf = (panel) => {
    if (Array.isArray(panel?.pendencias))
        return panel.pendencias.filter((item) => item && item.tipo);

    const caseData = panel?.case;
    if (!caseData) return [];

    return [
        ...(caseData.documentosCitados || [])
            .filter((item) => item && !item.documentId && item.descricao)
            .map((item) => ({ tipo: 'DOCUMENTO_FALTANTE', descricao: item.descricao })),
        ...(caseData.midias || []).filter(Boolean).map((item) => ({
            tipo: 'MIDIA',
            descricao: item.descricao || 'Mídia (áudio ou vídeo)'
        })),
        ...(caseData.procuracaoNosAutos === false
            ? [{ tipo: 'PROCURACAO', descricao: 'A procuração não está nos autos.' }]
            : [])
    ];
};

// Quem assina: o certificado (signers) com a habilitação no processo (lawyers) da mesma pessoa.
export const signersWithEnablement = (signers, lawyers) =>
    signers.map((signer) => {
        const lawyer = (lawyers || []).find(
            (item) => Number(item?.certificateId) === Number(signer.certificateId)
        );
        return { ...signer, habilitado: lawyer ? lawyer.habilitado ?? null : null };
    });

const partyNames = (parties) => (parties || []).map((party) => party?.nome).filter(Boolean);

export const caseSummary = (caseData) => {
    const processo = caseData?.processo || {};
    const vara = [processo.vara, processo.comarca].filter(Boolean).join(' · ');
    const ativo = partyNames(caseData?.partes?.ativo);
    const passivo = partyNames(caseData?.partes?.passivo);

    return {
        vara: vara || NOT_INFORMED,
        ativo: ativo.length ? ativo.join(', ') : NOT_INFORMED,
        passivo: passivo.length ? passivo.join(', ') : NOT_INFORMED,
        deadlineSource: caseData?.prazo?.fonte || null
    };
};

export const totalPages = (documents) => {
    if (!documents.length) return { total: 0, unknown: 0 };
    const unknown = documents.filter((document) => !(Number(document?.pages) > 0)).length;
    return {
        total: documents.reduce(
            (sum, document) => sum + (Number(document?.pages) > 0 ? Number(document.pages) : 0),
            0
        ),
        unknown
    };
};
